import { computed, Ref, ref, watch } from 'vue';
import to from 'await-to-js';
import { getApi, getEnv } from '../../../init';
import { POD_PAYMENT_TAB } from '../../../constants';
import type { StorageExpansionParams } from '../../../apis';

type PaymentType = POD_PAYMENT_TAB | string | null | undefined;

interface UsePayOptions {
  paymentType: Ref<PaymentType>;
  skuId?: Ref<string | number | null | undefined>;
  tradeNo?: Ref<string | null | undefined>;
  qrcodeApiParams?: Ref<Record<string, any> | null | undefined>;
  storageExpansion?: Ref<Record<string, any> | null | undefined>;
  env?: Ref<string | null | undefined>;
  api?: typeof getApi;
  onSuccess?: (result: any) => void;
  enablePolling?: boolean;
  watch?: boolean;
}

interface BuildPayUrlOptions {
  env?: string | null;
  payCode?: string | null;
  qrcodeUrl?: string | null;
  paymentType?: PaymentType;
}

const hostMap: Record<string, string> = {
  local: 'https://chore-dev.lingvisions.com',
  development: 'https://chore-dev.lingvisions.com',
  fat: 'https://chore-fat.lingvisions.com',
  production: 'https://www.ipoddy.cn',
};

function getPaymentType(paymentType?: PaymentType) {
  return paymentType || POD_PAYMENT_TAB.LINGDOU;
}

function isStoragePayment(paymentType?: PaymentType) {
  return getPaymentType(paymentType) === POD_PAYMENT_TAB.STORAGE;
}

export function normalizeStorageExpansion(value?: Record<string, any> | null) {
  const capacityGB = Number(value?.capacityGB);
  const months = Number(value?.months);

  if (!Number.isFinite(capacityGB) || !Number.isFinite(months) || capacityGB <= 0 || months <= 0) {
    return null;
  }

  return {
    capacityGB,
    months,
  };
}

export function buildPayUrl(options: BuildPayUrlOptions) {
  const env = options.env || 'development';
  const host = hostMap[env] || hostMap.development;
  return `${host}/single/pay/${options.payCode || ''}`;
}

export function usePay(options: UsePayOptions) {
  const qrcodeLoading = ref(true);
  const qrcodeError = ref(false);
  const payCode = ref();
  const qrcodeUrl = ref('');
  const price = ref();
  const api = options.api || getApi;
  const enablePolling = options.enablePolling !== false;
  const shouldWatch = options.watch !== false;
  let timer: ReturnType<typeof setInterval> | undefined;
  let startSequence = 0;

  const env = computed(() => options.env?.value || getEnv());
  const paymentType = computed(() => getPaymentType(options.paymentType.value));
  const storageExpansionParams = computed(() =>
    normalizeStorageExpansion(options.storageExpansion?.value)
  );
  const canStart = computed(() => {
    if (isStoragePayment(paymentType.value)) {
      return !!storageExpansionParams.value;
    }

    return !!options.skuId?.value;
  });
  const payUrl = computed(() =>
    buildPayUrl({
      env: env.value,
      payCode: payCode.value,
      qrcodeUrl: qrcodeUrl.value,
      paymentType: paymentType.value,
    })
  );

  function end() {
    clearInterval(timer);
    timer = undefined;
  }

  async function polling() {
    if (!payCode.value) {
      return;
    }

    const result = await api().tcQrCodeApi.getTransactionCode({
      code: payCode.value,
    });

    if (result.status === 'success') {
      end();
      options.onSuccess?.(result);
    }
  }

  async function loadLingdouPrice() {
    const result = await api().aiadTransactionsApi.getMagicBeanSkuPrice({
      skuId: options.skuId?.value,
    });
    return result.price;
  }

  async function loadStoragePrice(params: StorageExpansionParams) {
    const result = await api().aiadStorageApi.getCapacityPrice(params);
    return result.totalPrice;
  }

  async function loadPrice() {
    const storageParams = storageExpansionParams.value;

    if (isStoragePayment(paymentType.value) && storageParams) {
      return loadStoragePrice(storageParams);
    }

    return loadLingdouPrice();
  }

  async function loadLingdouPayCode() {
    const params = options.qrcodeApiParams?.value || {};
    const result = await api().tcQrCodeApi.transactionCode({
      skuId: options.tradeNo?.value ? undefined : options.skuId?.value,
      tradeNo: options.tradeNo?.value || undefined,
      ...params,
    });
    return {
      code: result,
      url: '',
    };
  }

  async function loadStoragePayCode(params: StorageExpansionParams) {
    const result = await api().tcQrCodeApi.createStorageExpansionQrCode(params);
    return {
      code: result,
      url: '',
    };
  }

  async function loadPayCode() {
    const storageParams = storageExpansionParams.value;

    if (isStoragePayment(paymentType.value) && storageParams) {
      return loadStoragePayCode(storageParams);
    }

    return loadLingdouPayCode();
  }

  async function start() {
    const currentSequence = ++startSequence;
    end();
    qrcodeError.value = false;

    if (!canStart.value) {
      qrcodeLoading.value = true;
      payCode.value = undefined;
      qrcodeUrl.value = '';
      price.value = undefined;
      return;
    }

    qrcodeLoading.value = true;

    const [err, result] = await to(Promise.all([loadPrice(), loadPayCode()]));

    if (currentSequence !== startSequence) {
      return;
    }

    if (err) {
      qrcodeError.value = true;
      payCode.value = undefined;
      qrcodeUrl.value = '';
    } else if (result) {
      const [nextPrice, qrcode] = result;
      price.value = nextPrice;
      payCode.value = qrcode.code;
      qrcodeUrl.value = qrcode.url;
    }

    qrcodeLoading.value = false;

    if (!err && payCode.value && enablePolling) {
      timer = setInterval(() => {
        polling();
      }, 1000);
    }
  }

  if (shouldWatch) {
    watch(
      [
        () => options.skuId?.value,
        () => options.tradeNo?.value,
        () => options.paymentType.value,
        () => options.qrcodeApiParams?.value,
        () => options.storageExpansion?.value?.capacityGB,
        () => options.storageExpansion?.value?.months,
      ],
      () => {
        start();
      },
      {
        immediate: true,
      }
    );
  }

  return {
    qrcodeLoading,
    qrcodeError,
    payCode,
    payUrl,
    price,
    start,
    end,
    polling,
  };
}
